import React from 'react';
import Page from '@/components/Page';
import { wrapper, useBillboardId } from '@/store';
import { getSubdomain, validateMobile, eventParams } from '@/utils/subdomain';
import { Stack } from '@mui/material';
import ErrorPage from '@/components/ErrorPage';
import { EventContextProvider } from '@/contexts/event/EventContext';
import useLoadEvent from '@/store/event/useLoadEvent';
import useGtmVariables from '@/hooks/useGtmVariables';
import { getSessionToken } from '@/contexts/session/sessionUtils';
import SessionHeartbeatProvider from '@/contexts/session/SessionHeartbeatProvider';
import { cleanUnusedTabs } from '@/utils/tab-id';
import { useSelector } from 'react-redux';

import TemplatePlus from '@/templates/plus';
import TemplateHybrid from '@/templates/hybrid';
import TemplateHiddenMap from '@/templates/hidden-map';

import { Templates } from '@/hooks/useBoletiaConfig';

import { Loader } from '@/hooks/useCallApiProcess';
import { Backdrop } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { skipToken } from '@reduxjs/toolkit/query';
import { useRouter } from 'next/dist/client/router';
import {
  useGetEventResumeByNamePublicQuery,
  useGetEventResumeByNamePrivateQuery,
  getEventResumeByNamePublic,
  getEventResumeByNamePrivate,
  getRunningQueriesThunk,
} from '@/store/event/event.api';

import QueueFair from '@/sections/analytics/QueueFair';

export default function HomePage({
  subDomain,
  isMobile,
  link,
  billboard,
  sessionToken,
}) {
  const { t } = useTranslation();
  const router = useRouter();
  const { event, error, template, isLoading } = useLoadEvent(subDomain, link);
  const [isClient, setIsClient] = React.useState(false);

  const { data: eventDataSsr } = useGetEventResumeByNamePublicQuery(
    subDomain || skipToken,
    {
      skip: router.isFallback,
    },
  );

  const shouldIndexEvent = () => {
    const endDate = event?.event_information?.event?.end_date;

    return endDate && new Date() < new Date(endDate);
  };

  React.useEffect(() => {
    setIsClient(true);
    cleanUnusedTabs();
  }, []);

  useBillboardId(billboard);
  useGtmVariables();

  if (error) {
    return (
      <ErrorPage
        title="Lo sentimos, el evento no existe"
        text="Te invitamos a buscar grandes evento en boletia.com"
      />
    );
  }

  return (
    <>
      {isLoading && (
        <Backdrop open={true}>
          <Loader msg={t('checkout.loadings.pleaseWait')} />
        </Backdrop>
      )}
      <Stack sx={{ width: '100%', height: '100%' }}>
        <Page
          ssr
          title={eventDataSsr?.name}
          sx={{
            width: '100%',
            height: '100%',
          }}
          meta={
            <>
              <meta name="description" content={`${eventDataSsr?.name}`} />

              <meta property="og:title" content={eventDataSsr?.name} />
              <meta property="og:type" content="article" />
              <meta
                property="og:url"
                content={`https://${eventDataSsr?.subdomain}.boletia.com`}
              />
              <meta property="og:image" content={eventDataSsr?.banner} />
              <meta property="og:site_name" content="Boletia" />
              <meta property="og:description" content={eventDataSsr?.name} />
              <meta property="og:locale" content="es_ES" />

              <meta name="twitter:card" content="summary_large_image" />
              <meta name="twitter:site" content="@boletia" />
              <meta name="twitter:creator" content="@boletia" />
              <meta name="twitter:domain" content="boletia.com" />
              <meta name="twitter:title" content={eventDataSsr?.name} />
              <meta name="twitter:description" content={eventDataSsr?.name} />
              <meta name="twitter:image" content={eventDataSsr?.banner} />
              <meta
                name="robots"
                content={
                  shouldIndexEvent() ? 'index, follow' : 'noindex, nofollow'
                }
              />
            </>
          }
        >
          <>
            {template && event ? (
              <EventContextProvider data={event}>
                <QueueFair />
                <SessionHeartbeatProvider
                  subdomain={subDomain}
                  sessionToken={sessionToken}
                >
                  {template === Templates.PLUS && (
                    <TemplatePlus isMobile={isMobile} />
                  )}
                  {template === Templates.HYBRID && (
                    <TemplateHybrid isMobile={isMobile} />
                  )}
                  {template === Templates.HIDDEN_MAP && (
                    <TemplateHiddenMap isMobile={isMobile} />
                  )}
                </SessionHeartbeatProvider>
              </EventContextProvider>
            ) : (
              <Backdrop open={true}>
                <Loader msg={'Cargando evento'} />
              </Backdrop>
            )}
          </>
        </Page>
      </Stack>
    </>
  );
}
export const getServerSideProps = wrapper.getServerSideProps(
  (store) =>
    async ({ req, res }) => {
      const isMobile = validateMobile(req);
      const subDomain = getSubdomain(req);
      const params = eventParams(req);

      const bots = [
        'facebookexternalhit',
        'facebookcatalog',
        'facebookbot',
        'twitterbot',
        'twitter',
        'whatsapp',
        'linkedinbot',
        'linkedin',
        'pinterest',
        'pinterestbot',
        'instagram',
        'telegrambot',
        'discord',
        'slack',
        'social',
        'bot',
        'crawler',
        'spider',
      ];

      const userAgent = req.headers['user-agent']?.toLowerCase() || '';
      const isBot = bots.some((bot) => userAgent.includes(bot));

      if (typeof subDomain === 'string') {
        try {
          if (isBot) {
            await store
              .dispatch(getEventResumeByNamePrivate.initiate(subDomain))
              .unwrap();
          }

          await store
            .dispatch(getEventResumeByNamePublic.initiate(subDomain))
            .unwrap();
        } catch (err) {
          console.error('Error al obtener datos del evento:', err);
        }
      }

      await Promise.all(store.dispatch(getRunningQueriesThunk()));

      let sessionToken = null;
      if (subDomain && !isBot) {
        try {
          const sessionData = await getSessionToken(subDomain);
          if (sessionData?.token) {
            sessionToken = sessionData.token;
          }
        } catch (error) {}
      }

      return {
        props: {
          subDomain,
          isMobile,
          link: params?.link ? params.link : null,
          billboard: params?.billboard ? params.billboard : null,
          sessionToken,
        },
      };
    },
);
